/**
 * THE authorization function (principle 2), `docs/design/00-contracts.md` §11.3.
 *
 * Pure, synchronous, no I/O — the data it needs is already on the `Actor`, which is what lets
 * the web app call it to decide which buttons to render (principle 7) while the API enforces
 * with the very same code. There is no second implementation anywhere.
 */
import type { Action, Actor, DenyReason, PolicyResult, QueryScope, Resource } from './types.js';
import { LOCKED_ACTIONS } from './default-permissions.js';
import {
  externalDecision,
  externalOperationDecision,
  type ExternalOperation,
} from './external-access.js';
import { guestCan } from './guest.js';
import { moduleGate } from './modules.js';
import { RULES } from './rules.js';
import { organiserSide } from './side.js';

const allow = (scope: QueryScope): PolicyResult => ({ allowed: true, scope });
const deny = (reason: DenyReason): PolicyResult => ({ allowed: false, reason });

const EVERYTHING: QueryScope = { kind: 'all' };

/**
 * HVA EN DELT PROGRAMPOST FAKTISK ÅPNER (ARTISTS.md §4.2).
 *
 * ------------------------------------------------------------------------------------------------
 * Spec-en lister åtte faner som delte og ÉN ting som aldri deles: Økonomi (kostnadslinjene). Den
 * er stengt i basen (`r_cost_lines_own_house`, 0406), ikke her.
 *
 * KONTRAKTEN ER DELT MED BYRÅET, for lesing OG skriving til den er signert (0133, 0152; sak 221.9,
 * «som i dag»). Motparten når den gjennom `contract:read`/`contract:update` i eget hus og RLS-en
 * på `in_shared_performance`. Den er TOPARTS: 0406 la en restriktiv policy på kontrakten, så ingen
 * annen deling — heller ikke spillestedets produksjonsadvance — kan åpne honoraret.
 *
 * `performance:delete` står heller ikke her, og det er hele forskjellen på å DELE et dokument og å
 * eie det: byrået fører rider og reisefølge sammen med festivalen, men river ikke ned konserten.
 *
 * Listen er lukket med vilje. En ny handling som skal deles er en avgjørelse noen tar, ikke noe som
 * sklir inn fordi den ligner på en som allerede står her.
 */
const SHARED_ACTIONS: ReadonlySet<Action> = new Set<Action>([
  'performance:read',
  'performance:list',
  'performance:update',
  'performance:comment',
  /*
   * ÅRGANGEN, og bare å LESE den.
   *
   * Advancens hode viser hvilken årgang konserten hører til, og overnattingsfanen bruker årgangens
   * første dag som utgangspunkt for kalenderen. Uten den svarer siden 404 på en opplysning som står
   * i klartekst på den samme skjermen — «God Nok 2026» — og kalenderen åpner ni måneder feil.
   *
   * `edition:update` er IKKE her: byrået fører advancen, ikke festivalens år.
   */
  'edition:read',

  /*
   * DEN ANDRE PARTENS OPPGAVE PÅ DEN DELTE PROGRAMPOSTEN (QA 22.09 punkt 8; sak 209, 24.09).
   *
   * Atle: «Jo, de kan kommentere og lukke hverandres, men legg det med i loggen.» Å lese, å
   * kommentere, og å fullføre eller åpne igjen. `task:update`, `task:assign` og `task:delete` er
   * IKKE her: tittel, frist, ansvarlig, avdeling og sletting er eierhusets, og basen sier det samme
   * (triggeren i 0388). Cellene gjelder fortsatt — rollen må ha dem i sitt eget hus.
   */
  'task:read',
  'task:comment',
  'task:complete',

  /*
   * ================================================================================================
   * TRÅDEN, SOM ER HELE GRUNNEN TIL AT DEN ER DELT (QA13 runde 2, #12a — 20. september).
   *
   * KOI Del 4 (migrering 0230) gjorde bookingtråden til ÉN rad eid av festivalen, delt med byrået
   * gjennom `threads.shared_with_organization_id`. RLS ble skrevet om samtidig og slipper begge hus
   * inn (`app.in_shared_thread`, 0231). Denne lista ble ikke rørt.
   *
   * Følgen, målt i basen 20. september: byrået så tråden i innboksen sin — innbokslista drives av
   * `thread_participants` og spør aldri `can()` per tråd — og fikk 404 i det de klikket.
   * `wrong_organization` står i `HIDE_EXISTENCE`, så avslaget kommer ut som «Fant ikke tråden».
   *
   * De tre handlingene er de tre en samtale ER: lese den, lese meldingene, skrive en. Å dele en
   * tråd og så nekte alle tre er å dele ingenting.
   *
   * `thread:update` og `thread:delete` er IKKE her, og det er med vilje: emnet og tråden selv
   * hører til huset som eier den. Gjesten snakker i den; hen rydder den ikke.
   */
  'thread:read',
  'message:list',
  'message:create',
]);

/**
 * DET PERSONLIGE HUSET (sak 216): det en person gjør for seg selv, og ingenting som bygger en
 * organisasjon. Lista er lukket per ressurs — en ny ressurs er ute til noen bestemmer noe annet —
 * og den står FØR de låste handlingene, fordi personen er superadmin i sitt eget hus.
 *
 * Kontakter og medlemskap kan leses fordi velgerne i oppgaver og chat spør dem; i huset står bare
 * personen selv. Det er ikke rollen som avgjør her, men at huset ikke er en organisasjon — samme slag
 * regel som `isAgencyHouse`.
 */
const PERSONAL_HOUSE_RESOURCES = new Set([
  'task',
  'event',
  'thread',
  'message',
  'notification',
  /* Utkast følger meldingene (D9): den som kan skrive i en tråd her, kan ha et utkast til den. */
  'draft',
  'tag',
  /*
   * EKSPORT (docs/EKSPORT.md §6, §9 «Artist og ekstern med innlogging»): en artist eller ekstern
   * uten medlemskap står i sitt personlige hus, og konsertene og delingene er hens egne. Cella
   * `export:create` i huset avgjør som ellers; katalogens `requires` avgjør hvilke eksporter.
   */
  'export',
]);
const PERSONAL_HOUSE_READS = new Set([
  'contact',
  'membership',
  'member_profile',
  'consent',
  'person',
  'user',
]);

function inAPersonalHouse(action: Action): boolean {
  const [resource = '', verb = ''] = action.split(':');
  if (PERSONAL_HOUSE_RESOURCES.has(resource)) return true;
  if (!PERSONAL_HOUSE_READS.has(resource)) return false;
  return (
    verb === 'list' ||
    verb === 'read' ||
    resource === 'person' ||
    resource === 'user' ||
    resource === 'consent' ||
    (resource === 'member_profile' && verb === 'update')
  );
}

/**
 * Five steps, in this order, denying by default at every one of them.
 *
 * 1. The resource must belong to the actor's organization.
 * 2. A locked action (§11.3.1) is permitted only to a superadmin. The matrix is **not** consulted,
 *    in either direction: a row cannot open this and a row cannot close it. This is the only place
 *    `superadmin` is special-cased.
 * 3. An action with no `Rule` is unreachable, whatever the data says.
 * 4. The organization's own matrix must allow it for the actor's role. This is the configurable
 *    step, and the only one an organization controls (decision 0001).
 * 5. The rule's record-level condition must hold; a permitted `list` is narrowed by its scope.
 *
 * Steps 3 and 4 are two independent gates and both must open: Settings can never open an action
 * the code has no rule for, and code can never open an action the organization switched off.
 */
export function can(actor: Actor, action: Action, resource: Resource): PolicyResult {
  if (resource.organizationId !== actor.organizationId) {
    /*
     * ARRANGØRENS ØKONOMI (D2): regelen om side, før delingen får si noe. En delt programpost åpner
     * aldri økonomien, uansett hva en framtidig `SHARED_ACTIONS` måtte si.
     */
    /* Bare på en rad som faktisk er delt: ellers er svaret 404-ens `wrong_organization`, som før. */
    if (
      resource.kind === 'record' &&
      resource.sharedWithActor === true &&
      action.startsWith('economy:')
    ) {
      const side = organiserSide(actor, 'organiser_economy', resource.organizationId);
      if (side !== true) return deny(side);
    }
    /*
     * ÉN NAVNGITT ÅPNING I STEG 1, og den er smalere enn den ser ut.
     *
     * `sharedWithActor` settes bare av en resolver som har spurt basen om det finnes en delt
     * advance begge hus er part i. Klienten kan ikke påstå den, og selv med den er det bare
     * `SHARED_ACTIONS` som slipper gjennom — resten faller tilbake til det samme avslaget som før.
     *
     * De fire stegene under gjelder uendret: rollen må fortsatt ha cella, og regelen må fortsatt
     * holde. Delingen sier hvilken RAD man når, ikke hva man får gjøre med den.
     */
    if (resource.kind !== 'record' || resource.sharedWithActor !== true) {
      return deny('wrong_organization');
    }
    if (!SHARED_ACTIONS.has(action)) return deny('action_not_shared');
  }

  /*
   * TOKEN UTENFRA (B3): organisasjonens AI-brytere og sperrelista, før rollen får si noe — også for
   * superadmin. Steg 1 står over, så en rad i et annet hus er fortsatt 404 og ikke en 403 som røper den.
   */
  if (actor.external !== undefined) {
    const external = externalDecision(actor, action, resource);
    if (external !== true) return deny(external);
  }

  /*
   * GJESTEN (sak 232) svarer fra hvitlista i `guest.ts`, og matrisen leses aldri: gjesten har ingen
   * rolle i huset. Steg 1 er tatt — gjesten handler inne i huset som delte.
   */
  if (actor.orgRole === 'guest') return guestCan(actor, action, resource);

  if (actor.organizationKind === 'personal' && !inAPersonalHouse(action)) {
    return deny('not_in_a_personal_house');
  }

  /*
   * MODULEN (G1, 253.6). Ressursen hører til en modul organisasjonen ikke har: nei, lesing som
   * skriving, også for superadmin — det er huset som mangler modulen, ikke rollen. Tabellen står i
   * `modules.ts`; menyen, rutene og API-et spør alle denne linja.
   */
  const gate = moduleGate(actor, action, resource);
  if (gate !== true) return deny(gate);

  const locked = LOCKED_ACTIONS.has(action);
  if (locked && actor.orgRole !== 'superadmin') return deny('superadmin_only');

  const rule = RULES.get(action);
  if (!rule) return deny('no_rule_matched');

  if (!locked && !actor.permissions.allows(actor.orgRole, action)) {
    return deny('role_not_permitted');
  }

  const condition = rule.when?.(actor, resource);
  if (condition !== undefined && condition !== true) return deny(condition);

  return allow(rule.scope?.(actor, resource) ?? EVERYTHING);
}

/**
 * Avslag som bare en bestemt RAD kan svare på. Spurt om samlingen blir de alltid nei — «er dette
 * min oppgave» har ikke et svar om «oppgaver» — og det er ikke det samme som at rollen mangler cella.
 */
const DEPENDS_ON_THE_ROW: ReadonlySet<DenyReason> = new Set<DenyReason>([
  'not_creator',
  'not_assignee',
  'not_participant',
  'not_in_department',
  'not_assigned_festival',
  'grant_missing',
  'grant_insufficient',
]);

/**
 * KAN DENNE ROLLEN NOEN GANG GJØRE DETTE — på i det minste én rad? (QA runde 5, H5 og H7.)
 *
 * Svaret en RUTE trenger før den vet hvilken rad den skal vise. `can()` om samlingen svarte nei for
 * hver celle med en radbetingelse: limited fikk «Ingen tilgang» på sin egen oppgave fra Oversikt, og
 * en ekstern bruker fikk det samme på programposten som var delt med henne — mens API-et svarte 200.
 * Ruta slipper dem inn; API-et avgjør raden, som alltid (prinsipp 7).
 *
 * Avslag som ikke handler om raden — rollen mangler cella, handlingen er låst, huset er feil slag —
 * er fortsatt nei.
 */
export function canReach(actor: Actor, action: Action, resource: Resource): boolean {
  const result = can(actor, action, resource);
  if (result.allowed) return true;
  return resource.kind === 'collection' && DEPENDS_ON_THE_ROW.has(result.reason);
}

/** Thrown by `assertCan()`. `apps/api` maps it to 403 or 404 via `HIDE_EXISTENCE` (§12.4). */
export class PolicyDenied extends Error {
  readonly action: Action;
  readonly reason: DenyReason;

  constructor(action: Action, reason: DenyReason) {
    super(`Policy denied "${action}": ${reason}.`);
    this.name = 'PolicyDenied';
    this.action = action;
    this.reason = reason;
  }
}

/**
 * `can()` for call sites that have nothing sensible to do with a denial. Returns the `QueryScope`
 * a permitted `list` must be narrowed by, so a repository cannot obtain the scope without having
 * passed the check that produced it.
 */
export function assertCan(actor: Actor, action: Action, resource: Resource): QueryScope {
  const result = can(actor, action, resource);
  if (!result.allowed) throw new PolicyDenied(action, result.reason);
  return result.scope;
}

/**
 * Varianten av en tillatt handling som bare tjenesten ser (`EXTERNAL_OPERATIONS`): en melding i en
 * tråd en annen organisasjon leser, et vedlegg en annen har lastet opp. Ingenting for nettsiden.
 */
export function assertExternalOperation(
  actor: Pick<Actor, 'external' | 'aiAccess'>,
  action: Action,
  operation: ExternalOperation,
): void {
  const decision = externalOperationDecision(actor, operation);
  if (decision !== true) throw new PolicyDenied(action, decision);
}

/**
 * Denials that must surface as **404**, not 403 (§12.4, C-21): the caller is outside the
 * resource's organization or could not otherwise learn that it exists, and a 403 would confirm
 * the id — which in this domain is itself commercially sensitive. Every other reason is a 403,
 * because the caller already knows the organization exists and hiding the failure produces
 * support tickets rather than security.
 *
 * Consumed in exactly one place, `PolicyGuard` in `apps/api`.
 */
export const HIDE_EXISTENCE: readonly DenyReason[] = Object.freeze([
  'wrong_organization',
  'not_a_member',
  'grant_missing',
  'grant_insufficient',
  'not_participant',
]);

export const hidesExistence = (reason: DenyReason): boolean => HIDE_EXISTENCE.includes(reason);
